import { __, formatPoints } from '../../utils';
import type { ProfileSummary } from './useProfileSummary';
import {
  Box,
  MeterBar,
  Skeleton,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type RowProps = {
  value: string;
  unit: string;
  /** tichý údaj vpravo - „2 sa vybavuje", „+80" */
  note?: React.ReactNode;
  isLoading?: boolean;
  children?: React.ReactNode;
};

/**
 * Jeden riadok súhrnu: veľké číslo, jednotka pri ňom a tichá poznámka vpravo.
 *
 * Kachličky, ktoré tu boli predtým, mali každá vlastný rám a podklad a v páse
 * širokom 300 px sa nemali kam poskladať - súhrn je odteraz tri riadky pod
 * sebou oddelené od zvyšku pásu linkou.
 */
const StatRow: React.FC<RowProps> = ({
  value,
  unit,
  note,
  isLoading,
  children,
}) => (
  <Stack gap={0.75}>
    <Stack direction='row' alignItems='baseline' gap={0.75}>
      {isLoading ? (
        <Skeleton variant='text' width={72} height={28} />
      ) : (
        <>
          <Typography
            component='span'
            sx={{
              fontSize: 24,
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {value}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {unit}
          </Typography>
          {!!note && <Box sx={{ ml: 'auto', flex: 'none' }}>{note}</Box>}
        </>
      )}
    </Stack>
    {children}
  </Stack>
);

type Props = {
  summary: ProfileSummary;
};

/**
 * Súhrn účtu - podnety, body, žetóny. Kreslí ho ľavý pás na desktope a karta
 * so súhrnom na mobile, preto sú dáta propom a nie ďalším dopytom.
 */
const ProfileStats: React.FC<Props> = ({ summary }) => (
  <Stack
    sx={{
      gap: 1.5,
      py: 1.75,
      borderTop: '1px solid',
      borderBottom: '1px solid',
      borderColor: 'dividerLight',
    }}
  >
    <StatRow
      value={summary.reportsCountLabel}
      unit={__('podnetov')}
      isLoading={summary.isLoadingReports}
      note={
        summary.reportsInProgress > 0 ? (
          <Typography variant='caption' color='text.secondary'>
            {summary.reportsInProgress} {__('sa vybavuje')}
          </Typography>
        ) : null
      }
    />

    <StatRow
      value={formatPoints(summary.points)}
      unit={__('bodov')}
      isLoading={summary.isLoadingPoints}
      note={
        summary.monthPoints !== 0 ? (
          <Typography
            variant='caption'
            fontWeight={700}
            color={summary.monthPoints > 0 ? 'success.main' : 'error.main'}
          >
            {summary.monthPoints > 0 ? '+' : ''}
            {formatPoints(summary.monthPoints)}
          </Typography>
        ) : null
      }
    />

    <StatRow
      value={formatPoints(summary.availableTokens)}
      unit={`${__('z')} ${formatPoints(summary.totalTokens)} ${__('žetónov')}`}
      isLoading={summary.isLoadingTokens}
    >
      <MeterBar
        value={
          summary.totalTokens
            ? (summary.availableTokens / summary.totalTokens) * 100
            : 0
        }
        color='primary.main'
        height={6}
        trackColor='dividerLight'
        label={`${summary.availableTokens} / ${summary.totalTokens}`}
      />
    </StatRow>
  </Stack>
);

export default ProfileStats;
